import { useEffect, useCallback, useRef } from 'react';
import { token_keys } from '../constants';

// ─── Session behaviour switch ──────────────────────────────────────────────────
// false → idle sessions expire. The token is refreshed only while the user is
//         actually interacting with the app, so IDLE_TIMEOUT of inactivity logs
//         them out while an active user stays signed in indefinitely.
// true  → the token is refreshed regardless of activity, which keeps every
//         session alive forever (the old "infinite token" behaviour).
// Flip this single constant to switch between the two.
const AUTO_EXTEND_SESSION = false;

// Session timeout configuration
const SESSION_CONFIG = {
  IDLE_TIMEOUT: 15 * 60 * 1000, // log out after 15 minutes without interaction
  WARNING_TIME: 10 * 60 * 1000, // refresh once the token is this close to expiring
  STATUS_CHECK_INTERVAL: 30 * 1000, // how often we re-evaluate idle/expiry
  ACTIVITY_WRITE_THROTTLE: 5 * 1000, // min gap between localStorage activity writes
  ACTIVITY_EVENTS: [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ]
};

export const useSessionTimeout = (onExtendSession, onLogout) => {
  const refreshInFlightRef = useRef(false);
  // Mirrors token_keys.last_activity. The ref is the fast path (mousemove fires
  // constantly); the write to localStorage is throttled to share it across tabs.
  // Starts at 0 on purpose — mounting is not activity, so a tab reopened after a
  // long absence has to fall back to the stored stamp and idle out.
  const lastActivityRef = useRef(0);
  const lastActivityWriteRef = useRef(0);

  // Calculate time until token expires
  const getTimeUntilExpiry = useCallback(() => {
    const issuedAt = localStorage.getItem(token_keys.issued_at);
    const expiresIn = localStorage.getItem(token_keys.expiration);

    if (!issuedAt || !expiresIn) return Infinity;

    const expiryTime = parseInt(issuedAt) + (parseInt(expiresIn) * 1000);
    return expiryTime - Date.now();
  }, []);

  // How long the user has been idle. Takes the most recent of this tab's ref and
  // the shared localStorage value, so activity in a sibling tab counts here too.
  const getIdleTime = useCallback(() => {
    const stored = parseInt(localStorage.getItem(token_keys.last_activity)) || 0;
    const lastActivity = Math.max(lastActivityRef.current, stored);
    return Date.now() - lastActivity;
  }, []);

  // Record activity (this tab's ref always, localStorage at most every 5s)
  const handleActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    if (now - lastActivityWriteRef.current >= SESSION_CONFIG.ACTIVITY_WRITE_THROTTLE) {
      lastActivityWriteRef.current = now;
      localStorage.setItem(token_keys.last_activity, now.toString());
    }
  }, []);

  // Refresh the token to extend the session. Guarded against overlapping calls:
  // the backend rotates the refresh token, so two in-flight refreshes would race
  // and leave us holding an already-invalidated one.
  const extendSession = useCallback(() => {
    if (refreshInFlightRef.current) {
      console.log("extendSession skipped - refresh already in flight");
      return;
    }
    if (!onExtendSession) return;

    console.log("Calling onExtendSession");
    refreshInFlightRef.current = true;
    Promise.resolve(onExtendSession()).finally(() => {
      refreshInFlightRef.current = false;
    });
  }, [onExtendSession]);

  // Extend the session when the token nears expiry, but only for a user who is
  // still around. AUTO_EXTEND_SESSION bypasses the activity check entirely.
  const scheduleAutoExtension = useCallback(() => {
    const idleTime = getIdleTime();

    if (!AUTO_EXTEND_SESSION && idleTime >= SESSION_CONFIG.IDLE_TIMEOUT) {
      // Idle user: don't renew. The idle check in checkSessionStatus logs out.
      console.log("Not extending session - user idle for", idleTime, "ms");
      return;
    }

    const timeUntilExpiry = getTimeUntilExpiry();
    console.log("Schedule auto extension - time until expiry:", timeUntilExpiry, "idle:", idleTime);

    if (timeUntilExpiry > SESSION_CONFIG.WARNING_TIME) {
      console.log("Not within warning period yet");
      return;
    }

    // Inside the warning window with a user who is still here → renew now. No
    // timer needed: the STATUS_CHECK_INTERVAL poll is what brings us here, and a
    // successful refresh pushes the expiry back out of the window.
    console.log("Extending session for active user");
    extendSession();
  }, [getIdleTime, getTimeUntilExpiry, extendSession]);

  // Periodic evaluation: log out if idle or expired, otherwise keep alive
  const checkSessionStatus = useCallback(() => {
    const timeUntilExpiry = getTimeUntilExpiry();
    const idleTime = getIdleTime();
    console.log("Session check - until expiry:", timeUntilExpiry, "idle:", idleTime);

    // Idle logout. This — not token expiry — is what enforces the 15 minutes,
    // since an active user's token gets renewed before it ever lapses.
    if (!AUTO_EXTEND_SESSION && idleTime >= SESSION_CONFIG.IDLE_TIMEOUT) {
      console.log("Idle timeout reached, logging out");
      if (onLogout) {
        onLogout();
      }
      return;
    }

    if (timeUntilExpiry <= 0) {
      // Backstop: token lapsed (e.g. a refresh failed, or the tab was asleep).
      console.log("Session expired, logging out");
      if (onLogout) {
        onLogout();
      }
      return;
    }

    scheduleAutoExtension();
  }, [getTimeUntilExpiry, getIdleTime, scheduleAutoExtension, onLogout]);

  // Cleanup function
  const cleanup = useCallback(() => {
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, handleActivity, true);
    });
  }, [handleActivity]);

  // Initialize session monitoring
  useEffect(() => {
    // Check if we have valid token data
    const hasTokenData = localStorage.getItem(token_keys.issued_at) && localStorage.getItem(token_keys.expiration);
    if (!hasTokenData) return;

    // The stamp is written at login and cleared at logout, so it is normally
    // already here — including when a tab is reopened after a long absence,
    // which SHOULD read as idle and log out. The only gap is a session that
    // predates this key (someone mid-session across the deploy); give those one
    // grace period rather than logging them out on sight.
    //
    // Deliberately not seeded from issued_at: the 401 interceptor rewrites that
    // on a background refresh, which would silently reset an idle user's clock.
    if (!localStorage.getItem(token_keys.last_activity)) {
      localStorage.setItem(token_keys.last_activity, Date.now().toString());
    }

    SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    checkSessionStatus();
    const statusCheckInterval = setInterval(checkSessionStatus, SESSION_CONFIG.STATUS_CHECK_INTERVAL);

    return () => {
      clearInterval(statusCheckInterval);
      cleanup();
    };
  }, [checkSessionStatus, cleanup, handleActivity]);

  return {
    extendSession
  };
};
